import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from '@auth';
import { Organization } from '@shared/models/user-profile';
import { RolesEnum } from '@shared/models/roles';
import { emptyUnsyncedSummary, UnsyncedEntityKind } from '@shared/models/preferred-organization';
import { OnlineOfflineService } from '@core/services/online-offine.service';
import { PreferredOrganizationService } from '@core/services/preferred-organization.service';
import { UnsyncedRecordsService } from '@core/services/unsynced-records.service';
import { ModuleActivationService } from '@core/services/module-activation.service';
import {
  PreferredOrgChangeGate,
  PreferredOrgChangeProgress,
  PreferredOrganizationChangeService
} from '@core/services/preferred-organization-change.service';

type SwitcherView = 'list' | 'explain' | 'confirm' | 'unsynced' | 'syncing';

interface UnsyncedPreviewGroup {
  kind: UnsyncedEntityKind;
  count: number;
  rows: any[];
}

/**
 * The Organizations panel inside the profile menu (US 27702), a port of the
 * wireframe's multi-view switcher:
 *
 * - list:     "Open now" + "Switch to" sections with the Offline-ready badge and
 *             local/server sublines, the switching info note with the "Local vs
 *             server" explainer link, and the preferred-organization footer.
 * - explain:  the Offline-ready vs Online-only comparison.
 * - confirm:  "Change preferred organization" — radio of the other organizations,
 *             the removal/sync warning, and the unsynced-records block.
 * - unsynced: 3-row previews per entity with "View all" into the filtered lists.
 * - syncing:  the four-step progress with "Run in background".
 */
@Component({
  selector: 'app-organization-switcher',
  templateUrl: './organization-switcher.component.html',
  styleUrls: ['./organization-switcher.component.scss']
})
export class OrganizationSwitcherComponent implements OnInit, OnDestroy {

  /** Close the whole profile menu. */
  @Output() closed = new EventEmitter<void>();
  /** Return to the main profile-menu panel. */
  @Output() backToMenu = new EventEmitter<void>();

  view: SwitcherView = 'list';
  organizations: Organization[] = [];
  isOnline = true;
  switching = false;

  /** confirm view state */
  pendingKeycloakOrgId = '';
  gate?: PreferredOrgChangeGate;
  gateLoading = false;

  /** unsynced view state */
  previews: UnsyncedPreviewGroup[] = [];
  previewsLoading = false;

  progress?: PreferredOrgChangeProgress;

  private subscriptions = new Subscription();

  constructor(
    public authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private onlineOfflineService: OnlineOfflineService,
    private moduleActivationService: ModuleActivationService,
    public preferredOrganizationService: PreferredOrganizationService,
    private unsyncedRecordsService: UnsyncedRecordsService,
    private changeService: PreferredOrganizationChangeService
  ) { }

  ngOnInit(): void {
    this.organizations = this.authService.userOrganizations;
    this.isOnline = this.onlineOfflineService.isOnline;

    this.subscriptions.add(this.onlineOfflineService.connectionChanged.subscribe({
      next: () => this.isOnline = this.onlineOfflineService.isOnline
    }));

    this.subscriptions.add(this.changeService.progress$.subscribe({
      next: progress => this.progress = progress
    }));

    // A change left running in the background re-opens on its progress.
    if (this.changeService.inProgress) {
      this.view = 'syncing';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  //#region shared accessors

  get currentOrganization(): Organization | undefined {
    return this.authService.currentOrganization;
  }

  get preferredName(): string {
    return this.preferredOrganizationService.preferred?.name ?? '';
  }

  get otherOrganizations(): Organization[] {
    const currentId = this.currentOrganization?.keycloakOrgId;
    return this.organizations.filter(org => org.keycloakOrgId !== currentId);
  }

  /** Organizations offered in the change dialog: everyone except the current preferred. */
  get changeCandidates(): Organization[] {
    const preferredId = this.preferredOrganizationService.preferred?.keycloakOrgId;
    return this.organizations.filter(org => org.keycloakOrgId !== preferredId);
  }

  get pendingOrganization(): Organization | undefined {
    return this.organizations.find(org => org.keycloakOrgId === this.pendingKeycloakOrgId);
  }

  get isAdmin(): boolean {
    return this.authService.isInRole(RolesEnum.SystemAdministrator);
  }

  /** The viewer may see record details only when permitted for the preferred tenant. */
  get userPermittedForPreferred(): boolean {
    return this.preferredOrganizationService.isPreferredPermittedFor(this.organizations);
  }

  get changeBlocked(): boolean {
    return !!this.gate && !this.gate.allowed;
  }

  /** Blocked with actual pending records — the only case the records note fits. */
  get blockedByRecords(): boolean {
    return this.changeBlocked && (this.gate?.summary.total ?? 0) > 0;
  }

  /**
   * Blocked for another reason (offline, or the pending check failed): say so
   * instead of showing a misleading "0 records must be synced".
   */
  get gateBlockReasonKey(): string | undefined {
    if (!this.changeBlocked || this.blockedByRecords) return undefined;
    return this.isOnline ? 'preferredOrg.confirm.blockedUnknown' : 'preferredOrg.confirm.blockedOffline';
  }

  get canApplyChange(): boolean {
    return !!this.pendingOrganization && !this.gateLoading && !this.changeBlocked && this.isOnline;
  }

  isPreferred(organization: Organization | undefined): boolean {
    const preferredId = this.preferredOrganizationService.preferred?.keycloakOrgId;
    return !!organization && !!preferredId && organization.keycloakOrgId === preferredId;
  }

  initialOf(organization: Organization | undefined): string {
    return (organization?.name || '?').charAt(0).toUpperCase();
  }

  sublineKey(organization: Organization | undefined): string {
    return this.isPreferred(organization) ? 'preferredOrg.offlineReadySubline' : 'preferredOrg.serverSubline';
  }

  //#endregion

  //#region list view

  async switchTo(organization: Organization): Promise<void> {
    if (!this.isOnline || this.switching) return;

    this.switching = true;
    try {
      await this.authService.switchTenantAsync(organization.keycloakOrgId);
      const suffix = this.translateService.instant(
        this.isPreferred(organization) ? 'preferredOrg.offlineReadyBadge' : 'preferredOrg.serverSubline');
      this.toast(this.translateService.instant('preferredOrg.toast.switched', { org: organization.name, mode: suffix }));
      this.closed.emit();
    } catch {
      this.toast(this.translateService.instant('preferredOrg.errors.switchFailed'));
    } finally {
      this.switching = false;
    }
  }

  showExplain(): void { this.view = 'explain'; }

  showList(): void {
    this.view = this.changeService.inProgress ? 'syncing' : 'list';
  }

  //#endregion

  //#region confirm view

  async showConfirm(): Promise<void> {
    this.view = 'confirm';
    this.pendingKeycloakOrgId = '';
    this.gateLoading = true;
    try {
      this.gate = await this.changeService.canChangeNowAsync();
    } catch {
      // Unknown pending state must block, never allow a destructive change.
      this.gate = { allowed: false, summary: emptyUnsyncedSummary() };
    } finally {
      this.gateLoading = false;
    }
  }

  pick(organization: Organization): void {
    this.pendingKeycloakOrgId = organization.keycloakOrgId;
  }

  async applyChange(): Promise<void> {
    const target = this.pendingOrganization;
    if (!target || !this.canApplyChange) return;

    this.view = 'syncing';
    await this.changeService.changePreferredAsync(target, 'change');
  }

  //#endregion

  //#region unsynced view

  async showUnsynced(): Promise<void> {
    this.view = 'unsynced';
    this.previewsLoading = true;
    try {
      const summary = this.gate?.summary ?? await this.unsyncedRecordsService.getGateSummaryAsync();
      const allUsers = this.isAdmin;
      const kinds: UnsyncedEntityKind[] = ['salesOrders', 'cashTransactions', 'closures'];
      const rowsPerKind = await Promise.all(
        kinds.map(kind => this.userPermittedForPreferred
          ? this.unsyncedRecordsService.getPendingRecordsAsync(kind, allUsers, 3).catch(() => [])
          : Promise.resolve([]))
      );
      this.previews = kinds.map((kind, index) => ({
        kind,
        count: summary[kind],
        rows: rowsPerKind[index]
      })).filter(group => group.count > 0 || group.rows.length > 0);
    } finally {
      this.previewsLoading = false;
    }
  }

  async viewAll(kind: UnsyncedEntityKind): Promise<void> {
    const preferred = this.preferredOrganizationService.preferred;

    // The lists live in the preferred organization — switch the view first if needed.
    if (preferred && this.authService.currentTenant !== preferred.tenantId) {
      if (!this.isOnline) return;
      try {
        await this.authService.switchTenantAsync(preferred.keycloakOrgId, { navigateHome: false });
      } catch {
        return;
      }
    }

    const route = await this.routeForKind(kind);
    this.closed.emit();
    this.router.navigate([route], { queryParams: { syncStatus: 'UnSynced' } });
  }

  private async routeForKind(kind: UnsyncedEntityKind): Promise<string> {
    if (kind === 'cashTransactions') return '/pos/cash-management';
    if (kind === 'closures') return '/pos/register-closures';
    const isV3 = await this.moduleActivationService.isPointOfSaleV3Enabled();
    return isV3 ? '/pos/sales-orders-v3' : '/data/offline-sales-orders';
  }

  rowCode(kind: UnsyncedEntityKind, row: any): string {
    if (kind === 'salesOrders') return row?.code ?? row?.document_code ?? this.shortId(row);
    if (kind === 'closures') return row?.cashRegister?.code ?? this.shortId(row);
    return row?.code ?? this.shortId(row);
  }

  rowDate(kind: UnsyncedEntityKind, row: any): Date | undefined {
    const raw = kind === 'salesOrders' ? (row?.orderDate ?? row?.document_date)
      : kind === 'cashTransactions' ? row?.recordedDate
        : row?.changeDate;
    return this.toDate(raw ?? row?.metadata?.createdDate);
  }

  rowStatus(row: any): string {
    return row?.metadata?.syncInfo?.syncStatus ?? '';
  }

  registerName(row: any): string {
    return row?.cashRegister?.name ?? (typeof row?.cashRegister === 'string' ? row.cashRegister : '') ?? '';
  }

  /** CashTransactionType: CashIn = 1, CashOut = 2. */
  cashTypeKey(row: any): string {
    return row?.transactionType === 2 ? 'preferredOrg.unsynced.cashOut' : 'preferredOrg.unsynced.cashIn';
  }

  /** CashRegisterStatus on the closure log: Open = 1, Closed = 2. */
  closureStatusKey(row: any): string {
    return row?.status === 2 ? 'preferredOrg.unsynced.closed' : 'preferredOrg.unsynced.open';
  }

  closureOpeningTime(row: any): Date | undefined {
    return this.toDate(row?.openingDetails?.opening_time
      ?? row?.openingDetails?.openingTime
      ?? row?.changeDate
      ?? row?.metadata?.createdDate);
  }

  private shortId(row: any): string {
    return (row?.metadata?.localId ?? '').slice(0, 8);
  }

  private toDate(value: any): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  //#endregion

  //#region syncing view

  runInBackground(): void {
    this.closed.emit();
  }

  dismissFailedChange(): void {
    this.changeService.resetProgress();
    this.showList();
  }

  //#endregion

  private toast(message: string): void {
    this.snackBar.open(message, '✖', { direction: this.translateService.instant('uiDirection') });
  }
}
